<script>
    import { preventDefault } from 'svelte/legacy';
    import { toast } from '@zerodevx/svelte-toast';
    /* Firebase */
    import { auth } from '@app/configs/firebase.js';
    import db from '@app/configs/firestore.js';
    import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
    /* 3rd Party Lib */
    import {
        BarLoader
    } from 'svelte-loading-spinners';
    import { onMount } from 'svelte';
    import { fade } from 'svelte/transition';
    import * as m from "@app/paraglide/messages.js";

    let iconikAppId = $state(""), 
        iconikAuthToken = $state(""),
        elevenLabsKey = $state(""), 
        dbWork = $state(), 
        userId = auth.currentUser.uid, 
        userDbRef = doc(db, "users", userId);

    async function updateIntegrationSettings(){
        dbWork = await updateDoc(userDbRef, {
            iconik : {
                appId : iconikAppId,
                authToken : iconikAuthToken
            },
            elevenLabs : {
                key : elevenLabsKey
            }
        });

        toast.push(m.options_integrations_saved(), {classes: ['toast-success']});
    }

    onMount(async () => {
        try {
            let userDoc = await getDoc(userDbRef);
            if (!userDoc.exists()){
                console.warn(`No user document exists for current user: ${userId}`);
                dbWork = await setDoc(userDbRef, {
                    iconik : {
                        appId : "",
                        authToken : ""
                    },
                    elevenLabs : {
                        key : ""
                    }
                });

                return;
            }

            let userInfo = userDoc.data();
            iconikAppId = userInfo.iconik ? userInfo.iconik.appId : "";
            iconikAuthToken = userInfo.iconik ? userInfo.iconik.authToken : "";
            elevenLabsKey = userInfo.elevenLabs ? userInfo.elevenLabs.key : "";            
        } catch(err){
            console.log(err, err.message);
            return err;
        }   
    });
</script>    

<div class="integrations-container">
    <div class="integrations-header">
        <h5 id="integrationSettingsTitle">{m.options_integrations_title()}</h5>
        <p class="text-muted mb-0">{m.options_integrations_desc()}</p>
    </div>

    {#await dbWork}
        <div class="loading-container" transition:fade={{ duration: 150 }}>
            <BarLoader size="200" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
        </div>       
    {:then}
        <form onsubmit={preventDefault(() => {dbWork = updateIntegrationSettings()})} aria-labelledby="integrationSettingsTitle">
            <div class="integration-card" transition:fade={{ duration: 150 }}>
                <div class="integration-header">
                    <img class="integration-logo" src="./assets/img/logos/iconik-logo.svg" alt={m.iconik_logo_alt()}>
                </div>
                <div class="integration-content">
                    <div class="mb-3">
                        <label for="iconikAppId" class="form-label">{m.options_integrations_application_id()}</label>
                        <div class="input-group">
                            <input 
                                type="password" 
                                class="form-control" 
                                id="iconikAppId" 
                                bind:value={iconikAppId} 
                                aria-labelledby="iconikAppIdLabel"
                            >
                        </div>
                    </div>
                    <div class="mb-0">
                        <label for="iconikAuthToken" class="form-label">{m.options_integrations_auth_token()}</label>
                        <div class="input-group">
                            <input 
                                type="password" 
                                class="form-control" 
                                id="iconikAuthToken" 
                                bind:value={iconikAuthToken} 
                                aria-labelledby="iconikAuthTokenLabel"
                            >
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="integration-card" transition:fade={{ duration: 150 }}>
                <div class="integration-header">
                    <img class="integration-logo" src="./assets/img/logos/elevenLabs.png" alt={m.eleven_labs_logo_alt()}>
                </div>
                <div class="integration-content">
                    <div class="mb-0">
                        <label for="elevenLabsKeyInput" class="form-label">{m.options_integrations_api_key()}</label>
                        <div class="input-group">
                            <input 
                                type="password" 
                                class="form-control" 
                                id="elevenLabsKeyInput" 
                                bind:value={elevenLabsKey} 
                                aria-labelledby="elevenLabsKeyInputLabel"
                            >
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="save-button-container">
                <button 
                    type="button" 
                    class="btn btn-primary" 
                    onclick={() => updateIntegrationSettings()} 
                    id="saveIntegrationSettingsButton"
                >
                    {m.options_integrations_save_settings()}
                </button>
            </div>
        </form>
    {:catch error}
        <div class="error-container" transition:fade={{ duration: 150 }}>
            <div class="error-icon">
                <i class="bi bi-exclamation-triangle"></i>
            </div>
            <p class="text-danger m-0">{m.options_integrations_error_loading()}</p>
        </div>	
    {/await}
</div>

<style>
    .integrations-container {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .integrations-header {
        margin-bottom: 0.5rem;
    }

    .integrations-header h5 {
        margin-bottom: 0.25rem;
        font-weight: 600;
    }

    .integration-card {
        background-color: var(--bs-light);
        border-radius: 0.5rem;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        margin-bottom: 1.5rem;
    }

    .integration-header {
        padding: 1rem;
        border-bottom: 1px solid rgba(0,0,0,0.1);
        display: flex;
        align-items: center;
    }

    .integration-logo {
        max-width: 150px;
        max-height: 40px;
        object-fit: contain;
    }

    .integration-content {
        padding: 1rem;
    }

    .loading-container {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 2rem;
        background-color: var(--bs-light);
        border-radius: 0.5rem;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    }

    .save-button-container {
        display: flex;
        justify-content: flex-end;
        margin-top: 1rem;
    }

    .error-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 2rem;
        text-align: center;
        background-color: var(--bs-light);
        border-radius: 0.5rem;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    }

    .error-icon {
        font-size: 2rem;
        margin-bottom: 1rem;
        color: var(--bs-danger);
    }
</style>
